// Optimistic concurrency for takeoff rows (db/21 TakeoffRooms.RowVer, db/37 TakeoffConditionsDrawn.RowVer).
//
// A save carries the version the editor loaded; the proc refuses it (VERSION_CONFLICT) if the row has moved on, so
// a stale tab cannot overwrite a newer save. But a version moving is not by itself a conflict: other writers in the
// SAME tab bump it without refreshing this row, and someone else may have changed a field this edit never touches.
// So on a refusal the editor re-reads the row and asks ONE question — did anything THIS edit changes move under it?
//   no  -> retry once on top of the fresh row (the person's fields laid over what is saved now)
//   yes -> refuse, say which fields, keep what is saved; nothing is silently overwritten either way.
// Pure: the page does the re-read and the retry.

export const isVersionConflict = (err) => /VERSION_CONFLICT/.test(String(err?.message ?? err ?? ""));

// Two stored values are the same if they print the same once "no value" and number formatting are normalised.
export function sameValue(a, b) {
  const norm = (v) => (v == null || v === "" ? null : typeof v === "number" ? v : (v !== "" && !Number.isNaN(+v) && typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v.trim()) ? +v : v));
  const x = norm(a), y = norm(b);
  if (typeof x === "number" && typeof y === "number") return Math.abs(x - y) < 1e-9;
  return x === y;
}

/** The keys whose values differ between two rows. */
export const changedFields = (before, after, keys) => keys.filter(k => !sameValue(before?.[k], after?.[k]));

const pick = (row, keys) => Object.fromEntries(keys.map(k => [k, row?.[k] ?? null]));

/**
 * After a refusal: may this edit be re-applied on top of the fresh row?
 *   touchedKeys  the fields this edit changes
 *   baseline     those fields as the editor last saw them (before the edit)
 *   fresh        the row as it is saved now (null = it no longer exists)
 *   mine         the row with the edit applied
 * -> { retry: true, merged }  |  { retry: false, gone: true }  |  { retry: false, changed: [keys] }
 * A field someone else changed to the very value this edit wants is not a conflict.
 */
export function planConflictRetry({ touchedKeys = [], baseline = {}, fresh, mine = {} }) {
  if (!fresh) return { retry: false, gone: true };
  const changed = touchedKeys.filter(k => !sameValue(fresh[k], baseline[k]) && !sameValue(fresh[k], mine[k]));
  if (changed.length) return { retry: false, changed };
  return { retry: true, merged: { ...fresh, ...pick(mine, touchedKeys) } };
}

const FIELD_LABELS = {
  RoomName: "name", RoomNumber: "number", RoomType: "type", LengthFt: "length", WidthFt: "width", HeightFt: "height", ACH: "ACH",
  PressureTarget: "pressure", AHUZoneID: "AHU zone", ClassificationID: "classification", RoomTemplateID: "template",
  WallKind: "wall system", WallItemID: "wall system", WallAssemblyID: "wall system", WallSystemsJson: "wall systems by position", CanvasX: "position", CanvasY: "position",
  CanvasRot: "rotation", SheetID: "drawing", PolygonJson: "outline", GeometryJson: "geometry", RoomID: "room", HeightFtOverride: "height override",
  QuantityOverride: "quantity override", Notes: "notes", DisplayOrder: "order",
};

/** One sentence for the person whose edit was refused. */
export function describeConflict(what, plan) {
  if (plan.gone) return `This ${what} was removed by someone else, so your change was not saved. The list has been refreshed.`;
  const fields = [...new Set((plan.changed || []).map(k => FIELD_LABELS[k] || k))].join(", ");
  return `Someone else changed this ${what}'s ${fields} since you loaded it, so your change was not saved and nothing was overwritten. The latest version is now showing — make the change again if you still want it.`;
}
